/**
 * Floating gallery of project screenshots — anyone with access can browse,
 * download, or delete. The right-column container in `Scene.tsx` stacks
 * this panel directly under the Messungen panel, so a long measurement
 * list simply pushes Bilder further down without overlap.
 *
 * The panel header is always visible and toggles a collapsible list.
 * Each item shows a tiny thumbnail (the same `url` straight from Spaces),
 * the author's initials, the creation date, and a sizeable ⬇ button.
 * "Alle herunterladen" downloads every screenshot in sequence.
 */

import { useState } from 'react'

import type { ServerScreenshot } from '../api/screenshotsClient'
import { colorForUser, initialsFor } from '../viewer/annotations'

interface Props {
  items: ServerScreenshot[]
  onDelete: (id: string) => void
  /** Triggers a real file download (Scene mints a presigned URL with
   * attachment Content-Disposition and clicks an invisible anchor). */
  onDownload: (item: ServerScreenshot) => void
  /** "Alle herunterladen" — Scene bundles every screenshot into a single
   * .zip file and triggers a download for that one file. */
  onDownloadAll: () => void
  /** True while the ZIP is being built / fetched — disables the button. */
  zipBusy?: boolean
  /** When true the gallery starts open. Pure UI state — not persisted. */
  defaultOpen?: boolean
  /** Force the panel to render even when there are zero screenshots. */
  alwaysVisible?: boolean
  /** Surface upload failures so the user knows the capture didn't land. */
  error?: string | null
  busy?: boolean
}

export function ScreenshotsPanel({
  items,
  onDelete,
  onDownload,
  onDownloadAll,
  zipBusy = false,
  defaultOpen = false,
  alwaysVisible = false,
  error = null,
  busy = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen)
  if (items.length === 0 && !alwaysVisible && !error && !busy) return null

  return (
    <div
      style={{
        // Positioned by the right-column container in Scene.tsx.
        width: '100%',
        flexShrink: 1,
        minHeight: 0,
        // Cap own height so the list scrolls inside instead of overflowing
        // the viewport when there are many screenshots.
        maxHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(20,20,20,0.92)',
        border: '1px solid #333',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
        color: '#eee',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: 13,
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: open ? '1px solid #2a2a2a' : 'none',
          flexShrink: 0,
          gap: 6,
        }}
      >
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            background: 'transparent',
            color: '#eee',
            border: 'none',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.5,
            cursor: 'pointer',
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ opacity: 0.7, fontSize: 10 }}>{open ? '▾' : '▸'}</span>
          <span>
            Bilder ({items.length}){busy ? ' …' : ''}
          </span>
        </button>
        {items.length > 1 && (
          <button
            onClick={() => {
              if (!zipBusy) onDownloadAll()
            }}
            disabled={zipBusy}
            title="Alle Screenshots als ZIP herunterladen"
            style={{
              background: 'transparent',
              border: '1px solid #444',
              color: '#aaa',
              cursor: zipBusy ? 'wait' : 'pointer',
              opacity: zipBusy ? 0.6 : 1,
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontFamily: 'inherit',
            }}
          >
            {zipBusy ? 'Wird gepackt…' : 'Alle als ZIP'}
          </button>
        )}
      </div>
      {error && (
        <div
          style={{
            padding: '6px 12px',
            background: 'rgba(220, 50, 50, 0.18)',
            color: '#fecaca',
            fontSize: 11,
            borderBottom: '1px solid #4a1a1a',
          }}
        >
          {error}
        </div>
      )}
      {open && items.length === 0 && !busy && !error && (
        <div style={{ padding: '10px 12px', fontSize: 11, opacity: 0.55 }}>
          Noch keine Screenshots. Klick 📷 in der Toolbar, um einen zu speichern.
        </div>
      )}
      {open && items.length > 0 && (
        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
          {items.map((s) => (
            <ScreenshotRow
              key={s.id}
              item={s}
              onDelete={onDelete}
              onDownload={onDownload}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ScreenshotRow({
  item,
  onDelete,
  onDownload,
}: {
  item: ServerScreenshot
  onDelete: (id: string) => void
  onDownload: (item: ServerScreenshot) => void
}) {
  const author = item.created_by
  const initials = initialsFor(author)
  const badgeColor = author ? colorForUser(author.id) : 0x888888
  const filename = downloadName(item)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        borderBottom: '1px solid #2a2a2a',
      }}
    >
      {item.url ? (
        <button
          onClick={() => onDownload(item)}
          title="Herunterladen"
          style={{
            flexShrink: 0,
            width: 44,
            height: 32,
            borderRadius: 4,
            overflow: 'hidden',
            background: '#000',
            border: '1px solid rgba(255,255,255,0.12)',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <img
            src={item.url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            draggable={false}
          />
        </button>
      ) : (
        <div
          style={{
            flexShrink: 0,
            width: 44,
            height: 32,
            background: '#2a2a2a',
            borderRadius: 4,
          }}
        />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={filename}
        >
          {formatShort(item.created_at)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: 0.7, fontSize: 10 }}>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: '#' + badgeColor.toString(16).padStart(6, '0'),
              color: '#fff',
              fontWeight: 700,
              fontSize: 7,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            title={author ? `${author.first_name} ${author.last_name}`.trim() || author.email : 'Unbekannt'}
          >
            {initials}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {humanSize(item.size_bytes)}
          </span>
        </div>
      </div>

      {item.url && (
        <button
          onClick={() => onDownload(item)}
          title="Herunterladen"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ec5ff',
            fontSize: 22,
            lineHeight: 1,
            padding: '0 6px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          ⬇
        </button>
      )}
      <button
        onClick={() => onDelete(item.id)}
        title="Löschen"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#888',
          cursor: 'pointer',
          fontSize: 14,
          padding: '0 4px',
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  )
}

function downloadName(s: ServerScreenshot): string {
  const d = new Date(s.created_at)
  const ts = Number.isNaN(d.getTime())
    ? s.id.slice(0, 8)
    : d.toISOString().replace(/[:.]/g, '-').slice(0, 19)
  return `screenshot-${ts}.jpg`
}

function formatShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function humanSize(bytes: number): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
